import { useEffect, useMemo, useState } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { hasAnyRole } from '../utils/permissionUtils';
import { isOrgAdminRole } from '../utils/roleUtils';
import { localISODate } from '../utils/dates';

const num = (v) => Number(v || 0);

/** Which money cards a person sees: the same roles that can open the screens behind them. */
export function moneyAccess(user) {
  const admin = !!user?.isSystemAdmin || (user?.roles || []).some(isOrgAdminRole);
  return {
    cash: admin || hasAnyRole(user, ['FIN_OPR', 'FIN_MGR']),
    receivables: admin || hasAnyRole(user, ['FIN_OPR', 'FIN_MGR', 'SALES_MGR']),
    payables: admin || hasAnyRole(user, ['FIN_OPR', 'FIN_MGR']),
    sales: admin || hasAnyRole(user, ['SALES_MGR', 'FIN_MGR'])
  };
}

/**
 * The owner's view of the money, top of the dashboard: cash and bank, what customers owe (and how much is overdue),
 * what is due to vendors this week, and this month's sales and gross profit against last month. Each card opens the
 * screen behind it; a person sees only the cards their role can open.
 */
function MoneyCards({ user, currency = 'INR' }) {
  const access = useMemo(() => moneyAccess(user), [user]);
  const [cash, setCash] = useState(null);
  const [receivables, setReceivables] = useState(null);
  const [payables, setPayables] = useState(null);
  const [sales, setSales] = useState(null);

  const money = useMemo(() => {
    const f = new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 });
    return (v) => f.format(num(v));
  }, [currency]);

  useEffect(() => {
    if (access.cash) axios.get('/api/dashboard/cash-and-bank').then((r) => setCash(r.data)).catch(() => {});
    if (access.receivables) axios.get('/api/dashboard/receivables-summary').then((r) => setReceivables(r.data)).catch(() => {});
    if (access.payables) axios.get('/api/accounts/payables').then((r) => setPayables(r.data)).catch(() => {});
    if (access.sales) {
      const now = new Date();
      const thisFrom = localISODate(new Date(now.getFullYear(), now.getMonth(), 1));
      const lastFrom = localISODate(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      // Last month up to the same day of the month, so the comparison is like for like
      const lastTo = localISODate(new Date(now.getFullYear(), now.getMonth() - 1, Math.min(now.getDate(), new Date(now.getFullYear(), now.getMonth(), 0).getDate())));
      Promise.all([
        axios.get('/api/reports/sales', { params: { from: thisFrom, to: localISODate(now) } }),
        axios.get('/api/reports/sales', { params: { from: lastFrom, to: lastTo } })
      ]).then(([a, b]) => setSales({ now: a.data.totals, before: b.data.totals })).catch(() => {});
    }
  }, [access]);

  const cards = [];
  if (access.cash && cash) {
    cards.push(
      <MoneyCard key="cash" to="/accounts/bank-accounts" title="Cash and bank" value={money(cash.total)}
        lines={[`Bank ${money(cash.bank)} · Cash ${money(cash.cash)}`]} />
    );
  }
  if (access.receivables && receivables) {
    const top = (receivables.topOverdueCustomers || []).slice(0, 2);
    cards.push(
      <MoneyCard key="collect" to="/accounts/receivables" title="To collect" value={money(receivables.totalOutstanding)}
        alert={num(receivables.totalOverdue) > 0 ? `${money(receivables.totalOverdue)} overdue` : null}
        lines={top.map((c) => `${c.customerName}: ${money(c.overdue)}`)} />
    );
  }
  if (access.payables && payables) {
    cards.push(
      <MoneyCard key="pay" to="/accounts/payables" title="To pay" value={money(payables.totalOwed)}
        alert={num(payables.overdue) > 0 ? `${money(payables.overdue)} overdue` : null}
        lines={[
          num(payables.dueSoon) > 0 ? `${money(payables.dueSoon)} due this week` : 'Nothing else due this week',
          payables.msmeBillsAtRisk > 0 ? `${payables.msmeBillsAtRisk} MSME bill${payables.msmeBillsAtRisk === 1 ? '' : 's'} near 45 days` : null
        ].filter(Boolean)} />
    );
  }
  if (access.sales && sales) {
    const nowSales = num(sales.now.sales);
    const before = num(sales.before.sales);
    const change = before > 0 ? Math.round(((nowSales - before) / before) * 100) : null;
    cards.push(
      <MoneyCard key="sales" to="/sales/reports" title="Sales this month" value={money(nowSales)}
        lines={[
          change == null ? 'Nothing to compare last month' : `${change >= 0 ? '▲' : '▼'} ${Math.abs(change)}% vs last month`,
          sales.now.margin != null ? `Gross profit ${money(sales.now.grossProfit)} (${Number(sales.now.margin).toLocaleString('en-IN', { maximumFractionDigits: 1 })}%)` : null
        ].filter(Boolean)} />
    );
  }

  if (cards.length === 0) return null;
  return <Row className="g-3 mb-4">{cards.map((c) => <Col key={c.key} xs={12} sm={6} lg={3}>{c}</Col>)}</Row>;
}

function MoneyCard({ to, title, value, alert, lines }) {
  return (
    <Card as={Link} to={to} className="h-100 text-decoration-none text-reset">
      <Card.Body className="py-3">
        <div className="small text-muted text-uppercase" style={{ letterSpacing: '.04em' }}>{title}</div>
        <div className="fs-4 fw-semibold">{value}</div>
        {alert && <div className="small text-danger fw-semibold">{alert}</div>}
        {lines.map((l) => <div key={l} className="small text-muted">{l}</div>)}
      </Card.Body>
    </Card>
  );
}

export default MoneyCards;
